import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/design-system/button';
import { SegmentedControl, SegmentedItem } from '@/design-system/segmented-control';
import { toast } from '@/design-system/toaster';
import {
  useUrlNumber,
  useUrlParam,
  useUrlPatch,
  useUrlSearch,
} from '@/hooks/urlState';

import { warehouseApi } from '../../api/warehouse.api';
import { ClientSelect, HeaderActions } from '../../components/pickers';
import { HubRequired, LoadError, PageHeader } from '../../components/ui';
import { downloadBlob, todayJakarta, toCsv } from '../../format';
import { useLoad } from '../../hooks/useLoad';
import {
  csvAmount,
  LOCATION_TYPE_LABEL,
  UTILIZATION_BASES,
  UTILIZATION_BASIS,
  UTILIZATION_FILTERS,
  type UtilizationFilter,
  ZONE_OCCUPANCY_STATUS,
} from '../../occupancy';
import type { LocationUtilization, UtilizationBasis } from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { ATTENTION_LIMIT, AttentionStrip } from './AttentionStrip';
import { HubOccupancyCard } from './HubOccupancyCard';
import {
  CHILDREN_LIMIT,
  type LoadChildren,
  ZONE_PAGE_SIZES,
  ZoneList,
} from './ZoneList';

/** Header row of the location CSV, in the basis' unit. */
export const locationCsvHeader = (basis: UtilizationBasis): string[] => {
  const { unit } = UTILIZATION_BASIS[basis];
  return [
    'Lokasi',
    'Tipe',
    `Terpakai (${unit})`,
    `Kapasitas (${unit})`,
    `Tersisa (${unit})`,
    'Utilisasi (%)',
    'Status',
    'Bin tanpa kapasitas',
  ];
};

/** Locations per export request (the server's max) and the most pages fetched. */
export const EXPORT_LIMIT = 48;
export const EXPORT_MAX_PAGES = 10;

/**
 * The location rows as CSV cells (the list's columns, fullest first).
 * Numbers carry a dot decimal (`1.2`) so a spreadsheet reads them as numbers.
 */
export const locationCsvRows = (
  rows: LocationUtilization[],
  basis: UtilizationBasis
): unknown[][] => [
  locationCsvHeader(basis),
  ...rows.map((l) => [
    l.name,
    LOCATION_TYPE_LABEL[l.type] ?? l.type,
    csvAmount(l.used, basis),
    csvAmount(l.capacity, basis),
    csvAmount(l.free, basis),
    l.pct === null ? '' : Number(l.pct.toFixed(1)),
    ZONE_OCCUPANCY_STATUS[l.status]?.label ?? l.status,
    l.binsWithoutCapacity,
  ]),
];

/** `utilisasi-lokasi-{hub}-{basis}-{DDMMYYYY}.csv` (WIB). */
const csvName = (hub: string, basis: UtilizationBasis): string => {
  const [y, m, d] = todayJakarta().split('-');
  const slug = hub.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `utilisasi-lokasi-${slug || 'hub'}-${basis}-${d}${m}${y}.csv`;
};

const UTIL_VALUES = UTILIZATION_FILTERS.map((f) => f.value);
/** Changing the filter starts the location list over at page 1. */
const RESET_ZPAGE = ['zpage'] as const;
/** Changing the basis starts the location list and "Perlu tindakan" over at page 1. */
const RESET_PAGES = ['zpage', 'apage'] as const;

/**
 * Utilisasi lokasi: how full each location of the hub is against its
 * capacity, on one basis — volume (m³), weight (kg) or units — at every
 * level (location → rak → bin), after JD Edwards' location inquiry. The hub
 * card and the locations that need action always cover the hub's storage
 * locations; below them one server page of the locations (fullest first,
 * searchable by name, filterable by utilization); a
 * location opens its racks and a rack its bins in place.
 */
const StorageOccupancyPage: React.FC = () => {
  const { hubID, hub, ready, refreshToken } = useWarehouse();
  const enabled = ready && hubID !== null;
  const { searchParams, patch } = useUrlPatch();
  const rawClient = Number(searchParams.get('clientID'));
  const clientID =
    Number.isInteger(rawClient) && rawClient > 0 ? rawClient : null;
  const clientName = clientID
    ? searchParams.get('clientName') || `Client #${clientID}`
    : null;
  const [basis, setBasis] = useUrlParam<UtilizationBasis>('basis', 'volume', {
    allowed: UTILIZATION_BASES,
    resetParams: RESET_PAGES,
  });
  const [util, setUtil] = useUrlParam<UtilizationFilter>('util', 'all', {
    allowed: UTIL_VALUES,
    resetParams: RESET_ZPAGE,
  });
  const range =
    UTILIZATION_FILTERS.find((f) => f.value === util) ?? UTILIZATION_FILTERS[0];
  const search = useUrlSearch('q', {
    resetPage: true,
    pageKey: 'zpage',
    delay: 350,
  });
  const [page, setPage] = useUrlNumber('zpage', 1, { min: 1 });
  const [rawLimit, setLimit] = useUrlNumber('zlimit', ZONE_PAGE_SIZES[0], {
    min: 1,
    resetPage: true,
    pageKey: 'zpage',
  });
  const limit = ZONE_PAGE_SIZES.includes(rawLimit) ? rawLimit : ZONE_PAGE_SIZES[0];
  const q = search.committed;
  // "Perlu tindakan" pages on its own; search / filter never narrow it.
  const [apage, setApage] = useUrlNumber('apage', 1, { min: 1 });

  // Another hub starts both lists over at page 1 (not the first pick on load).
  const lastHub = useRef(hubID);
  useEffect(() => {
    const prev = lastHub.current;
    lastHub.current = hubID;
    if (prev !== null && prev !== hubID) {
      patch((p) => {
        p.delete('zpage');
        p.delete('apage');
      });
    }
  }, [hubID, patch]);

  // What each part of the answer depends on, so paging one part dims only it.
  const scopeKey = `${hubID}|${clientID ?? ''}|${basis}|${refreshToken}`;
  const listKey = `${scopeKey}|${q}|${util}|${page}|${limit}`;
  const attentionKey = `${scopeKey}|${apage}`;
  const zones = useLoad(
    (signal) =>
      warehouseApi
        .getLocationUtilization(
          {
            hubID: hubID as number,
            clientID,
            basis,
            search: q,
            minPct: range.minPct,
            maxPct: range.maxPct,
            page,
            limit,
            attentionPage: apage,
            attentionLimit: ATTENTION_LIMIT,
          },
          { signal }
        )
        .then((res) => ({ res, listKey, attentionKey })),
    [hubID, clientID, basis, q, range, page, limit, apage, refreshToken],
    enabled
  );
  const listShown = zones.data?.listKey === listKey;
  const attentionShown = zones.data?.attentionKey === attentionKey;
  // A reload (same keys) dims both; a new page of one part dims that part only.
  const listLoading = zones.loading && (!listShown || attentionShown);
  const attentionLoading = zones.loading && (!attentionShown || listShown);

  const loadChildren = useCallback<LoadChildren>(
    (parentID, childPage, signal) =>
      warehouseApi.listStorageOccupancyChildren(
        {
          hubID: hubID as number,
          parentID,
          clientID,
          basis,
          sort: 'hot',
          page: childPage,
          limit: CHILDREN_LIMIT,
        },
        { signal }
      ),
    [hubID, clientID, basis]
  );

  const hubName = hub?.name ?? `Hub #${hubID}`;
  const data = zones.data?.res ?? null;
  const pagination = data?.pagination ?? null;
  const attentionPagination = data?.attentionPagination ?? null;
  const hubHasLocations =
    !!data && data.hub.locationCount + data.excluded.length > 0;

  // A page past the end (locations removed, a hand-edited URL) → the last page.
  useEffect(() => {
    if (pagination && pagination.total > 0 && page > pagination.lastPage) {
      setPage(pagination.lastPage);
    }
  }, [pagination, page, setPage]);
  useEffect(() => {
    if (
      attentionPagination &&
      attentionPagination.total > 0 &&
      apage > attentionPagination.lastPage
    ) {
      setApage(attentionPagination.lastPage);
    }
  }, [attentionPagination, apage, setApage]);

  // Every location of the hub on the basis (not the page, the search or the
  // filter), up to 10 pages of 48.
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    if (!data || hubID === null) return;
    setExporting(true);
    try {
      const all: LocationUtilization[] = [];
      let lastPage = 1;
      for (let p = 1; p <= Math.min(lastPage, EXPORT_MAX_PAGES); p += 1) {
        const res = await warehouseApi.getLocationUtilization({
          hubID,
          clientID,
          basis,
          page: p,
          limit: EXPORT_LIMIT,
        });
        all.push(...res.locations);
        lastPage = res.pagination.lastPage;
      }
      downloadBlob(
        new Blob(['﻿' + toCsv(locationCsvRows(all, basis))], {
          type: 'text/csv;charset=utf-8',
        }),
        csvName(hub?.code || hubName, basis)
      );
      toast.success(
        lastPage > EXPORT_MAX_PAGES
          ? `CSV utilisasi lokasi berhasil di-download (${all.length} lokasi pertama)`
          : 'CSV utilisasi lokasi berhasil di-download'
      );
    } catch (err) {
      toast.error(
        `Gagal export CSV utilisasi lokasi: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5 p-6">
      <PageHeader
        eyebrow="Warehouse · Dashboard"
        title="Utilisasi lokasi"
        description="Seberapa penuh tiap lokasi dibanding kapasitasnya — pilih ukurannya: volume, berat, atau unit."
        // Per hub: no "Semua hub" that would only say "pilih hub dulu".
        actions={
          <HeaderActions allowAll={false}>
            <ClientSelect
              className="w-44"
              value={clientID ? { id: clientID, name: clientName as string } : null}
              onChange={(c) =>
                patch((p) => {
                  if (c) {
                    p.set('clientID', String(c.id));
                    p.set('clientName', c.name);
                  } else {
                    p.delete('clientID');
                    p.delete('clientName');
                  }
                  p.delete('zpage');
                  p.delete('apage');
                })
              }
              allowClear
              placeholder="Semua client"
              aria-label="Client"
            />
            <Button
              onClick={exportCsv}
              disabled={!enabled || !hubHasLocations || exporting}
              data-testid="occ-export"
              leftIcon={<i className="ri-download-2-line text-[14px]" />}
            >
              {exporting ? 'Menyiapkan CSV…' : 'Export CSV'}
            </Button>
          </HeaderActions>
        }
      />

      {!enabled ? (
        <HubRequired />
      ) : zones.error && !data ? (
        <LoadError message={zones.error.message} onRetry={zones.reload} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[13px] text-text-sub-600">Ukur berdasarkan</span>
            <SegmentedControl
              value={basis}
              onValueChange={(v: string) => {
                // Clicking the picked item again would clear it: keep it.
                if (v) setBasis(v as UtilizationBasis);
              }}
              aria-label="Ukur utilisasi berdasarkan"
              data-testid="occ-basis"
            >
              {UTILIZATION_BASES.map((b) => (
                <SegmentedItem key={b} value={b} data-testid={`occ-basis-${b}`}>
                  {UTILIZATION_BASIS[b].label}
                </SegmentedItem>
              ))}
            </SegmentedControl>
          </div>
          <HubOccupancyCard hubName={hubName} data={data} clientName={clientName} />
          {data && (
            <AttentionStrip
              items={data.attention}
              pagination={data.attentionPagination}
              thresholds={data.thresholds}
              basis={data.basis}
              loading={attentionLoading}
              onPage={setApage}
            />
          )}
          <ZoneList
            zones={data?.locations ?? null}
            pagination={pagination}
            thresholds={data?.thresholds ?? null}
            basis={data?.basis ?? basis}
            hubHasZones={hubHasLocations}
            search={search}
            util={util}
            onUtil={setUtil}
            loading={listLoading}
            error={zones.error?.message ?? null}
            onRetry={zones.reload}
            onPage={setPage}
            onLimit={setLimit}
            loadChildren={loadChildren}
            resetKey={`${hubID}|${clientID ?? ''}`}
          />
        </>
      )}
    </div>
  );
};

export default StorageOccupancyPage;
